import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutHeroImage, aboutTeamImage } from '@/mocks/article-images';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u4p5n8l97n3eas3hd0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '做爱指南',
    url: 'https://xianxiangyong.cn',
    description: '专业做爱健康知识与亲密关系科普平台，从科学、健康与情感角度深度解析成人亲密关系。',
    sameAs: [],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          {/* Hero */}
          <section className="relative w-full h-[300px] md:h-[400px] flex items-center justify-center overflow-hidden">
            <img
              src={aboutHeroImage}
              alt="关于我们 - 做爱指南"
              title="关于我们 - 做爱指南品牌介绍"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
            <div className="relative z-10 text-center px-4">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-xl mx-auto">
                以科学为本，以关怀为心，为成年伴侣提供专业、可靠的亲密关系知识
              </p>
            </div>
          </section>

          <div className="w-full px-4 md:px-6 py-8">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />
            </div>
          </div>

          {/* Brand Introduction */}
          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                品牌介绍
              </h2>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  做爱指南是一个专注于成人性健康与亲密关系科普的专业平台。我们相信，健康、愉悦的性生活是成年人整体幸福的重要组成部分，而通往这一目标的最佳路径是科学知识与开放态度。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  我们的内容团队由性健康教育专家、心理咨询师和医学顾问组成，致力于从生理学、心理学和情感关系三个维度，为读者提供全面、准确、实用的亲密关系知识。每一篇文章都经过严格的专业审核，确保信息的科学性和可靠性。
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  我们深知这一话题的敏感性和重要性。因此，我们始终坚持尊重、包容、不评判的内容原则，为每一位成年读者创造一个安全、舒适的学习空间。无论你处于亲密关系的哪个阶段，这里都有适合你的知识与支持。
                </p>
              </div>
            </div>
          </section>

          {/* Mission & Values */}
          <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                我们的使命
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className="ri-book-open-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">知识传播</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    用科学、准确的知识消除误区，帮助每一位读者建立正确的性观念和亲密关系认知。
                  </p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                    <i className="ri-heart-2-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">情感关怀</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    以温暖、包容的态度陪伴每一位读者的学习与探索之旅，让性不再是一个令人尴尬的话题。
                  </p>
                </div>
                <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className="ri-star-line text-xl"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">品质提升</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    帮助伴侣增进情感联结、提升亲密关系质量，让每一段关系都能向着更健康的方向发展。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Professional Reputation */}
          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-6">
                专业声誉
              </h2>
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="flex-shrink-0">
                    <img
                      src={aboutTeamImage}
                      alt="专业团队"
                      title="做爱指南专业团队"
                      className="w-full md:w-64 h-48 object-cover rounded-lg"
                    />
                  </div>
                  <div>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                      我们的专家团队由来自医学、心理学、性教育等领域的专业人士组成。每一位撰稿人都拥有相关领域的学术背景和丰富的实践经验。
                    </p>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-3">
                      所有内容均参考最新的学术研究成果和权威医学指南，经过多轮专业审核后方可发布。我们承诺不使用未经证实的偏方或民间说法来误导读者。
                    </p>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      我们与多家专业的性健康教育机构和心理咨询中心保持合作关系，确保内容的专业性和前沿性。读者反馈也是我们持续改进的重要动力。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Contact Form */}
          <section id="contact" className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-2xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-3 text-center">
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 text-center mb-8">
                如果你有任何疑问、建议或合作意向，欢迎通过以下表单与我们取得联系。
              </p>

              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8"
              >
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                  >
                    <option value="">请选择主题</option>
                    <option value="general">一般咨询</option>
                    <option value="content">内容建议</option>
                    <option value="cooperation">合作意向</option>
                    <option value="feedback">反馈意见</option>
                    <option value="other">其他</option>
                  </select>
                </div>

                <div className="mb-5">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors resize-none"
                    placeholder="请输入你的留言（500字以内）"
                  ></textarea>
                </div>

                {formStatus === 'success' && (
                  <div className="mb-4 p-3 bg-secondary-50 text-secondary-700 text-sm rounded-md border border-secondary-200">
                    <i className="ri-checkbox-circle-line mr-1"></i>
                    感谢你的留言！我们会尽快回复。
                  </div>
                )}

                {formStatus === 'error' && (
                  <div className="mb-4 p-3 bg-accent-50 text-accent-700 text-sm rounded-md border border-accent-200">
                    <i className="ri-error-warning-line mr-1"></i>
                    {formError}
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full sm:w-auto px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>
              </form>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}